// The Training card on a QEW's own tag page. Pure: records in, card out.
// Words: QEW_TRAINING_STATUS_WORDS.md. Mockup: MOCKUP_QEW_TRAINING_CARD_APPROVED.html (2026-10-05).
// Built now as a ready piece. It lands on the page when the QEW dashboard does.
//
// Rules:
// * Newest record per node wins. Dates come from the record.
// * Inside 30 days of expiry a node turns amber. Past expiry it turns red and opens for retake.
// * Nodes go in order. The first node not current is the one to take next.
// * Four nodes. Node 4 is the field demonstration: no quiz, does not expire.
// * Once Nodes 1 to 3 have been done, gloves open and stay open.
// * The page header ("In training" or "Qualified, Active") comes from the tag
//   status, which also needs Node 4 and LOTO. An expired node shows only here.
// * How a node was earned never shows here.

import { GLOVE_NODES, NODES, isCurrentOn, type NodeId, type TrainingRecord } from "./training";

export const WARNING_DAYS = 30;

export type CardNodeState = "current" | "expiringSoon" | "expired" | "next" | "waiting";

export interface CardNode {
  nodeId: NodeId;
  label: string;
  state: CardNodeState;
  completedOn: string | null;
  expiresOn: string | null;
  daysLeft: number | null;
  line: string;
}

export interface QewTrainingCard {
  nodes: CardNode[];
  currentCount: number;
  /** All four nodes done at some point, current or not. */
  trainingComplete: boolean;
  /** "1 of 4", "4 of 4", or "3 of 4 current" once training is complete. */
  countLabel: string;
  /** Nodes 1 to 3 done at some point. Never closes. */
  glovesOpen: boolean;
}

type RecordDates = Pick<TrainingRecord, "nodeId" | "completedOn" | "expiresOn">;

/** Whole days from one YYYY-MM-DD to another. */
export function daysBetween(fromIso: string, toIso: string): number {
  const a = Date.parse(`${fromIso}T00:00:00Z`);
  const b = Date.parse(`${toIso}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000);
}

/** 2027-09-17 reads 09/17/2027. */
function us(iso: string): string {
  const [y, m, d] = iso.split("-");
  return y && m && d ? `${m}/${d}/${y}` : iso;
}

export function qewTrainingCard(records: readonly RecordDates[], today: string): QewTrainingCard {
  const latest = new Map<NodeId, RecordDates>();
  for (const r of records) {
    const have = latest.get(r.nodeId);
    if (!have || r.completedOn > have.completedOn) latest.set(r.nodeId, r);
  }

  let firstGap: number | null = null;
  const nodes: CardNode[] = NODES.map((node, i) => {
    const label = `Node ${i + 1} · ${node.nodeName}`;
    const r = latest.get(node.nodeId);
    const base = { nodeId: node.nodeId, label, completedOn: r?.completedOn ?? null, expiresOn: r?.expiresOn ?? null };

    if (r && r.expiresOn === null) {
      return { ...base, state: "current" as const, daysLeft: null, line: `Completed ${us(r.completedOn)} · Does not expire` };
    }
    if (r && r.expiresOn && isCurrentOn(r.expiresOn, today)) {
      const daysLeft = daysBetween(today, r.expiresOn);
      if (daysLeft <= WARNING_DAYS) {
        return {
          ...base,
          state: "expiringSoon" as const,
          daysLeft,
          line: `Expires in ${daysLeft} ${daysLeft === 1 ? "day" : "days"} · ${us(r.expiresOn)} · Retake with your instructor`,
        };
      }
      return { ...base, state: "current" as const, daysLeft, line: `Completed ${us(r.completedOn)} · Expires ${us(r.expiresOn)}` };
    }

    const isFirstGap = firstGap === null;
    if (isFirstGap) firstGap = i;
    const after = `Node ${(firstGap ?? i) + 1}`;
    if (r && r.expiresOn) {
      return {
        ...base,
        state: "expired" as const,
        daysLeft: null,
        line: isFirstGap ? `Expired ${us(r.expiresOn)} · Retake with your instructor` : `Expired ${us(r.expiresOn)} · Retake after ${after}`,
      };
    }
    return isFirstGap
      ? { ...base, state: "next" as const, daysLeft: null, line: node.quizId ? `Next up · Quiz ${i + 1}` : "Next up · Two demo JSPs" }
      : { ...base, state: "waiting" as const, daysLeft: null, line: `Opens after Node ${i}` };
  });

  const currentCount = nodes.filter((n) => n.state === "current" || n.state === "expiringSoon").length;
  const trainingComplete = NODES.every((n) => latest.has(n.nodeId));
  const total = NODES.length;
  return {
    nodes,
    currentCount,
    trainingComplete,
    countLabel: trainingComplete && currentCount < total ? `${currentCount} of ${total} current` : `${currentCount} of ${total}`,
    glovesOpen: GLOVE_NODES.every((n) => latest.has(n)),
  };
}

function esc(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const DOT: Record<CardNodeState, (i: number) => string> = {
  current: () => "✓",
  expiringSoon: () => "!",
  expired: (i) => String(i + 1),
  next: (i) => String(i + 1),
  waiting: (i) => String(i + 1),
};

/** The card as HTML, for the tag page. Styles: .qtc-* in styles.css. */
export function qewTrainingCardHtml(card: QewTrainingCard): string {
  const rows = card.nodes
    .map((n, i) => {
      const last = i === card.nodes.length - 1;
      const doneLine = n.state === "current" || n.state === "expiringSoon";
      return `<div class="qtc-row"><div class="qtc-rail"><div class="qtc-dot ${n.state}">${DOT[n.state](i)}</div>${
        last ? "" : `<div class="qtc-line${doneLine ? " done" : ""}"></div>`
      }</div><div class="qtc-text ${n.state}"><b>${esc(n.label)}</b><small>${esc(n.line)}</small></div></div>`;
    })
    .join("");
  const soon = card.nodes.find((n) => n.state === "expiringSoon");
  const expired = card.nodes.find((n) => n.state === "expired");
  const note = expired
    ? `<div class="note red">${esc(expired.label.split(" · ")[0])} expired. Retake it with your instructor at the next class.</div>`
    : soon && soon.expiresOn
      ? `<div class="note">${esc(soon.label.split(" · ")[0])} expires ${esc(us(soon.expiresOn))}. Ask your instructor about the next class.</div>`
      : "";
  return `<section class="qtc"><h3>Training <span>${esc(card.countLabel)}</span></h3><div class="qtc-chain">${rows}</div></section>${note}`;
}
